import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Badge, Button, Input, Label, LoadingState } from '@buzzbee/ui';
import { useState } from 'react';
import { del, errorMessage, get, post, put } from '../lib/api';
import { useWorkspace } from '../lib/session';

type Field = { name: string; label: string; secret: boolean; required: boolean; placeholder: string; helpUrl: string | null; help?: string };
type Account = {
  spec: { label: string; usedFor: string; helpUrl: string | null; fields: Field[] };
  connected: boolean;
  usable: boolean;
  hints: Record<string, string | null> | null;
  webhooksConfigured: boolean | null;
  webhooksMessage: string | null;
  voiceConnected: boolean;
};
type Telephony = {
  twilio: Account;
  voiceRuntime: 'stub' | 'twilio' | 'elevenlabs';
  webhooks: { voice: string; status: string; sms: string };
};

/** Settings, Phone: connect the workspace's own Twilio account and number. The voice stays the product's. */
export function OwnPhoneNumber({ canEdit }: { canEdit: boolean }) {
  const brand = useWorkspace().brand.name;
  const q = useQuery({ queryKey: ['telephony'], queryFn: () => get<Telephony>('/api/settings/telephony') });
  const qc = useQueryClient();
  const [values, setValues] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const done = async (text: string) => {
    setMessage({ ok: true, text });
    setValues({});
    await qc.invalidateQueries();
  };
  // Only fields typed in are sent: blank keeps what is stored.
  const fields = Object.fromEntries(Object.entries(values).filter(([, v]) => v.trim()));
  const save = useMutation({ mutationFn: () => put<{ message: string }>('/api/settings/telephony/twilio', { fields }), onSuccess: (r) => done(r.message) });
  const test = useMutation({ mutationFn: () => post<{ ok: boolean; message: string }>('/api/settings/telephony/twilio/test'), onSuccess: (r) => setMessage({ ok: r.ok, text: r.message }) });
  const remove = useMutation({ mutationFn: () => del('/api/settings/telephony/twilio'), onSuccess: () => done(`Disconnected. ${brand}’s number is used again.`) });
  if (!q.data) return <LoadingState />;
  const { twilio: account, voiceRuntime, webhooks } = q.data;
  const error = save.error ?? remove.error ?? test.error;
  const missing = account.spec.fields.some((f) => f.required && !fields[f.name] && !account.hints?.[f.name]);
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {account.connected ? <Badge variant={account.usable ? 'success' : 'default'}>{account.usable ? 'Connected' : 'Needs reconnecting'}</Badge> : <Badge>Using {brand}&apos;s number</Badge>}
        {account.spec.helpUrl ? (
          <a className="ml-auto text-sm underline" href={account.spec.helpUrl} target="_blank" rel="noreferrer">
            Open {account.spec.label}
          </a>
        ) : null}
      </div>
      <p className="text-sm text-muted-foreground">
        {account.spec.usedFor} The AI voice stays {brand}&apos;s. You pay {account.spec.label} directly for the number, calls and texts; calls still count toward your plan&apos;s
        minutes.
      </p>
      {account.connected && voiceRuntime === 'elevenlabs' && !account.voiceConnected ? (
        <p className="text-sm text-muted-foreground">Your AI receptionist starts answering this number when you finish setup.</p>
      ) : null}
      {account.connected && account.webhooksConfigured === false ? (
        <Alert variant="warning" title={`Point your number at ${brand}`}>
          {account.webhooksMessage} In the {account.spec.label} console, set this number&apos;s incoming message webhook to <code className="figures break-all">{webhooks.sms}</code>
          {voiceRuntime === 'elevenlabs' ? null : (
            <>
              , its incoming call webhook to <code className="figures break-all">{webhooks.voice}</code> and its call status callback to{' '}
              <code className="figures break-all">{webhooks.status}</code>
            </>
          )}{' '}
          (HTTP POST).
        </Alert>
      ) : null}
      <div className="grid gap-4 sm:grid-cols-2">
        {account.spec.fields.map((f) => {
          const hint = account.hints?.[f.name];
          const id = `own-twilio-${f.name}`;
          return (
            <div key={f.name} className="space-y-1.5">
              <Label htmlFor={id}>{f.label}</Label>
              <Input
                id={id}
                type={f.secret ? 'password' : 'text'}
                autoComplete="off"
                disabled={!canEdit}
                placeholder={hint ? (f.secret ? `Saved, ending in ${hint}` : hint) : f.placeholder}
                value={values[f.name] ?? ''}
                onChange={(e) => setValues({ ...values, [f.name]: e.target.value })}
              />
              {f.help ? <p className="text-xs text-muted-foreground">{f.help}</p> : null}
            </div>
          );
        })}
      </div>
      {error ? <Alert variant="destructive">{errorMessage(error)}</Alert> : message ? <Alert variant={message.ok ? 'success' : 'destructive'}>{message.text}</Alert> : null}
      {canEdit ? (
        <div className="flex flex-wrap gap-2">
          <Button type="button" onClick={() => save.mutate()} loading={save.isPending} disabled={missing || !Object.keys(fields).length}>
            {account.connected ? 'Save changes' : 'Test and connect'}
          </Button>
          {account.connected ? (
            <>
              <Button type="button" variant="outline" onClick={() => test.mutate()} loading={test.isPending}>
                Test
              </Button>
              <Button type="button" variant="ghost" onClick={() => remove.mutate()} loading={remove.isPending}>
                Disconnect
              </Button>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
